/**
 * Phase 5 batch 1 Slice 4 — user-application linkage on sign-in.
 *
 * When a verified user signs in, the engine needs an applicationId
 * to hydrate from. This module owns the "first sign-in" provisioning
 * flow:
 *
 *   1. Read users/{uid}. If missing, create the user doc with
 *      { email, displayName?, createdAt: serverTimestamp(),
 *        defaultApplicationId? }.
 *   2. Read the user's defaultApplicationId. If absent, mint a fresh
 *      applicationId, create applications/{appId} with
 *      { ownerUserId: uid, schemaVersion, status: 'draft', createdAt,
 *        updatedAt, savedAt, answers: {} }, and write
 *      users/{uid}.defaultApplicationId = newAppId.
 *   3. Return { applicationId, isNewApplication }.
 *
 * v1 is one user, one application. Multi-app per user is Phase 6+.
 *
 * Security-rules contract: ownerUserId and createdAt are immutable on
 * applications/{appId} once created. The rules deny any update that
 * mutates either field. This module's create paths set
 * createdAt = serverTimestamp() so the rule's
 * `request.resource.data.createdAt == request.time` check passes.
 *
 * Source-cited Firebase APIs (verified against
 * `node_modules/@firebase/firestore/dist/index.d.ts`):
 *   - getDoc(ref) -> DocumentSnapshot
 *   - setDoc(ref, data) -> Promise<void>
 *   - updateDoc(ref, data) -> Promise<void>
 *   - serverTimestamp() -> FieldValue   (resolves to request.time
 *                                        on the server, satisfying the
 *                                        immutability rule)
 *
 *   https://firebase.google.com/docs/firestore/manage-data/add-data
 *   https://firebase.google.com/docs/reference/security/cloud-firestore#timestamp
 */

import { AOS_V1_SCHEMA_VERSION } from '@inside-us-aos/domain';
import {
  type Firestore,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';

const USERS_COLLECTION = 'users';
const APPLICATIONS_COLLECTION = 'applications';

/**
 * Slim shape of the Firebase Auth `User` we depend on. Avoids a hard
 * import of firebase/auth into a module the persistence layer also
 * uses (smaller bundle when only one half of the adapter is needed).
 */
export interface AuthIdentity {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export interface LinkageResult {
  applicationId: string;
  isNewApplication: boolean;
  isNewUser: boolean;
}

export interface LinkageOptions {
  /**
   * Catalog version to stamp on a freshly-created application.
   * Defaults to `AOS_V1_SCHEMA_VERSION`.
   */
  schemaVersion?: string;
  /**
   * Override the applicationId minter. Useful for tests; in
   * production the default uses `<uid>-<timestamp>` so the id is
   * traceable and order-correlated with the user.
   */
  generateApplicationId?: (identity: AuthIdentity) => string;
}

const defaultGenerateApplicationId = (identity: AuthIdentity): string =>
  `${identity.uid}-${Date.now()}`;

/**
 * Resolves the user's default applicationId, creating both the user
 * doc and a fresh application doc when needed. Returns the resolved
 * id plus boolean flags noting which docs were newly created.
 */
export const linkUserToApplication = async (
  firestore: Firestore,
  identity: AuthIdentity,
  options: LinkageOptions = {},
): Promise<LinkageResult> => {
  const schemaVersion = options.schemaVersion ?? AOS_V1_SCHEMA_VERSION;
  const generateApplicationId =
    options.generateApplicationId ?? defaultGenerateApplicationId;

  const userRef = doc(firestore, USERS_COLLECTION, identity.uid);
  const userSnap = await getDoc(userRef);
  let isNewUser = false;
  let defaultApplicationId: string | undefined;

  if (!userSnap.exists()) {
    isNewUser = true;
    const userPayload: Record<string, unknown> = {
      email: identity.email ?? '',
      createdAt: serverTimestamp(),
    };
    if (identity.displayName !== null && identity.displayName !== undefined) {
      userPayload.displayName = identity.displayName;
    }
    await setDoc(userRef, userPayload);
  } else {
    const data = userSnap.data() as { defaultApplicationId?: string };
    if (typeof data.defaultApplicationId === 'string') {
      defaultApplicationId = data.defaultApplicationId;
    }
  }

  if (defaultApplicationId !== undefined) {
    // Verify the application doc still exists. A dangling pointer
    // happens when ops deletes the application collection (e.g.,
    // operator nuke for data migration) but leaves the user's
    // defaultApplicationId pointer behind. Without this check, every
    // sign-in returns the orphan id, hydrate gets an empty snapshot,
    // and the autosave's setDoc gets rejected by the Firestore
    // security rule (ownerUserId not set on create). Symptom: user
    // fills the form, signs out and back in, sees empty form, and
    // nothing ever lands in Firestore. Found 2026-05-22.
    const liveCheck = await getDoc(
      doc(firestore, APPLICATIONS_COLLECTION, defaultApplicationId),
    );
    if (liveCheck.exists()) {
      return {
        applicationId: defaultApplicationId,
        isNewApplication: false,
        isNewUser,
      };
    }
    // Pointer is dangling — fall through to create a fresh
    // application below. The user pointer is overwritten further
    // down in the same call.
  }

  const newApplicationId = generateApplicationId(identity);
  const appRef = doc(firestore, APPLICATIONS_COLLECTION, newApplicationId);
  // Per security rules: ownerUserId == auth.uid, createdAt == request.time,
  // status == 'draft' on create. serverTimestamp() satisfies the time check.
  const appPayload: Record<string, unknown> = {
    ownerUserId: identity.uid,
    schemaVersion,
    status: 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    savedAt: new Date(0).toISOString(),
    answers: {},
  };
  await setDoc(appRef, appPayload);

  // Update the user pointer. updateDoc is fine because the user doc
  // exists at this point (we just created it or it was already
  // present). The security rule allows update when email + createdAt
  // are unchanged — adding a new field passes.
  await updateDoc(userRef, { defaultApplicationId: newApplicationId });

  return {
    applicationId: newApplicationId,
    isNewApplication: true,
    isNewUser,
  };
};
